/*
 * EntityPageInner3.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-10-06 in huWaWi3 33.1.0
 * Copyright (c) 2022, 2023 HUDORA GmbH
 */

import { CommandBar, ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { HdErrorBoundary3 } from '@hudora/hd-error-boundary';
import { useSchemaPanel } from '@hudora/hd-react-components';
import Stack from '@mui/material/Stack';
import { DocumentNode } from 'graphql';
import React, { ReactNode } from 'react';
import { IEntity } from 'types';

import { useAuditPanel } from '../../hooks/useAuditPanel';
import { useEditPanel3 } from '../../hooks/useEditPanel3';
import { useRohdatenPanel } from '../../hooks/useRohdatenPanel';
import { AnmerkungCard } from '../AnmerkungenCard';
import { EntityCard } from '../EntityCard';

interface IEntityPageInner3Props {
  entity: IEntity;
  schemaName: string;
  mutation?: DocumentNode;
  commandBarItems?: ICommandBarItemProps[];
  children?: React.ReactNode;
  renderEntity?: (entity, schemaName) => ReactNode;
  renderAnmerkung?: (entity, schemaName) => ReactNode;
  renderRightSidebar?: (entity, schemaName) => ReactNode;
  renderBottom?: (entity, schemaName) => ReactNode;
  children?: React.ReactNode;
}

/** Stellt eine Entity (Kundengruppe, einen Verband, eine Versandanweisung etc.)
 *  mit Edit Funktionalität usw. in einer Command-Bar dar.
 *
 * Die darüber liegende Navigation mit zB einer Tab-Bat (Pivot) ist nicht Teil dieser Komponente.
 *
 * Wird vom JSON-Schema gesteuert.
 * Standardmässig gibt es Audit, Rohdaten, Schema und evtl. Edit in der Command Bar.
 * Anmerkungsfunktionalität direkt neben der Entity.
 * Die eigentliche Darstellung erledigt <EntityRender/>
 * */
export const EntityPageInner3 = (props: IEntityPageInner3Props) => {
  if (!props.entity) {
    return null;
  }
  const renderEntity = props.renderEntity || ((e, s) => <EntityCard entity={e} schemaName={s} />);
  const renderAnmerkung =
    props.renderAnmerkung ||
    ((e, s) => {
      return e?.designator ? <AnmerkungCard forDesignator={e?.designator} /> : null;
    });
  const renderRightSidebar = props.renderRightSidebar || ((e, s) => null);
  const renderBottom = props.renderBottom || ((e, s) => null);

  if (props.mutation && props.entity._rev === undefined) {
    throw new Error('Es wird eine Mutation ermöglicht, aber Entity hat kein _rev. Das ist ein Bug.');
  }

  return (
    <HdErrorBoundary3>
      <Stack spacing="1em">
        <EntityCommandBar3
          entity={props.entity}
          schemaName={props.schemaName}
          mutation={props.mutation}
          commandBarItems={props.commandBarItems}
        />
        <Stack direction={'row'} spacing="1em">
          <div>{renderEntity(props.entity, props.schemaName)}</div>
          <div>{renderAnmerkung(props.entity, props.schemaName)}</div>
          <div>{renderRightSidebar(props.entity, props.schemaName)}</div>
        </Stack>
        <div>{renderBottom(props.entity, props.schemaName)}</div>
        <div>{props.children}</div>
      </Stack>
    </HdErrorBoundary3>
  );
};

interface IEntityCommandBarProps3 {
  entity: IEntity;
  schemaName: string;
  mutation?: DocumentNode;
  commandBarItems?: ICommandBarItemProps[];
}

/** Implementiert die Icon-Leiste und zugehörige Funktionalität
 * */
export const EntityCommandBar3 = (props: IEntityCommandBarProps3) => {
  const [editItem, editPanel] = useEditPanel3({
    entity: props.entity,
    mutation: props.mutation,
    schemaName: props.schemaName,
  });

  const { auditItem, auditPanel } = useAuditPanel(props.entity?.designator, props.entity);
  const [schemaItem, schemaPanel] = useSchemaPanel(props.schemaName);
  const [rohdatenItem, rohdatenPanel] = useRohdatenPanel(props?.entity, undefined, props.schemaName);
  const items = [...(props.commandBarItems ?? []), editItem].filter(Boolean);
  return (
    <Stack>
      <CommandBar items={items} farItems={[auditItem, rohdatenItem, schemaItem]} />
      {editPanel}
      {auditPanel}
      {rohdatenPanel}
      {schemaPanel}
    </Stack>
  );
};
